import { useLayoutEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';
import { shortDate } from '../stats/statsData';

const PITCH = 18; // px per cell including the 2px margin on each side
const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const SHADE_OPACITY = [0, 0.35, 0.65, 1];

const KIND_TEXT = {
  trained: 'trained',
  extra: 'extra session on a rest day',
  rest: 'planned rest',
  missed: 'missed',
  pending: 'planned today',
  future: 'coming up',
  none: 'no plan data',
};

function cellLabel(c) {
  const parts = [shortDate(c.date)];
  if (c.planDay) parts.push(c.planDay);
  parts.push(KIND_TEXT[c.kind] || '');
  if (c.sets) parts.push(`${c.sets} sets`);
  return parts.filter(Boolean).join(' · ');
}

/* The visual square inside a cell button. The button itself is the full
 * pitch, so there are no dead gaps between targets. */
function CellMark({ t, c, amber, tick, selected }) {
  const base = {
    position: 'absolute', inset: 2, borderRadius: 3, boxSizing: 'border-box',
    outline: selected ? `2px solid ${t.text}` : 'none', outlineOffset: 1,
  };
  if (c.kind === 'trained' || c.kind === 'extra') {
    return (
      <span style={{ ...base, overflow: 'hidden', border: c.isToday ? `1.5px solid ${t.text}` : 'none' }}>
        <span style={{ position: 'absolute', inset: 0, background: t.accent, opacity: SHADE_OPACITY[c.shade] }} />
        {c.kind === 'extra' && (
          // Corner tick: a session on a planned rest day.
          <span style={{
            position: 'absolute', top: 0, right: 0, width: 0, height: 0,
            borderTop: `6px solid ${tick}`, borderLeft: '6px solid transparent',
          }} />
        )}
      </span>
    );
  }
  if (c.kind === 'missed') return <span style={{ ...base, border: `1.5px solid ${amber}` }} />;
  if (c.kind === 'pending') return <span style={{ ...base, border: `1.5px dashed ${t.accent}` }} />;
  if (c.kind === 'rest') {
    return (
      <span style={{ ...base, display: 'flex', alignItems: 'center', justifyContent: 'center', border: c.isToday ? `1.5px solid ${t.textMuted}` : 'none' }}>
        <span style={{ width: 3, height: 3, borderRadius: '50%', background: t.textMuted }} />
      </span>
    );
  }
  if (c.kind === 'future') return <span style={{ ...base, border: `1px solid ${t.divider}` }} />;
  return <span style={{ ...base, background: t.surfaceAlt, border: `1px solid ${t.divider}` }} />;
}

/* 7 rows (Mon-Sun) x one column per week, scrollable sideways, newest week
 * on the right and scrolled into view on open. */
export function TrainingHeatmap({ t, columns, selected, onSelect, onOlder, colors }) {
  const scrollRef = useRef(null);
  // Distance from the right edge, kept when older weeks are prepended so the
  // view doesn't jump; null on mount = start at the newest week.
  const fromRight = useRef(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = fromRight.current === null ? el.scrollWidth : el.scrollWidth - fromRight.current;
  }, [columns.length]);

  const older = () => {
    const el = scrollRef.current;
    if (el) fromRight.current = el.scrollWidth - el.scrollLeft;
    onOlder();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
      <div aria-hidden="true" style={{ paddingTop: 16, flexShrink: 0 }}>
        {DAY_INITIALS.map((d, i) => (
          <div key={i} style={{
            height: PITCH, width: 12, fontSize: 9, fontWeight: 700, color: t.textFaint,
            display: 'flex', alignItems: 'center',
          }}>{d}</div>
        ))}
      </div>
      <div ref={scrollRef} style={{ overflowX: 'auto', flex: 1, minWidth: 0, WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', width: 'max-content' }}>
          {onOlder && (
            <button type="button" onClick={older} aria-label="Show older weeks" title="Show older weeks" style={{
              width: 34, height: PITCH * 7, marginRight: 4, marginTop: 16, borderRadius: 8,
              background: 'transparent', border: `1px dashed ${t.cardBorder}`, color: t.textMuted,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}><ChevronLeft size={16} /></button>
          )}
          <div role="group" aria-label="Training calendar">
            <div style={{ position: 'relative', height: 16 }}>
              {columns.map((col, i) => col.month && (
                <span key={col.monday} style={{
                  position: 'absolute', left: i * PITCH + 2, top: 0, fontSize: 10, fontWeight: 600,
                  color: t.textMuted, whiteSpace: 'nowrap',
                }}>{col.month}</span>
              ))}
            </div>
            <div style={{ display: 'flex' }}>
              {columns.map(col => (
                <div key={col.monday} style={{ display: 'flex', flexDirection: 'column' }}>
                  {col.days.map(c => (
                    <button key={c.date} type="button" onClick={() => onSelect(c.date)}
                      aria-label={cellLabel(c)} aria-pressed={selected === c.date} title={cellLabel(c)}
                      style={{
                        position: 'relative', width: PITCH, height: PITCH, padding: 0, margin: 0,
                        background: 'transparent', border: 'none', cursor: 'pointer', display: 'block',
                      }}>
                      <CellMark t={t} c={c} amber={colors.amber} tick={colors.tick} selected={selected === c.date} />
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* What the marks mean; text labels so it reads without colour. */
export function HeatmapLegend({ t, colors }) {
  const sq = { width: 12, height: 12, borderRadius: 3, display: 'inline-block', boxSizing: 'border-box', position: 'relative', flexShrink: 0 };
  const item = { display: 'inline-flex', alignItems: 'center', gap: 5 };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', fontSize: 11, color: t.textMuted, marginTop: 10 }}>
      <span style={item}>
        {[1, 2, 3].map(s => (
          <span key={s} style={{ ...sq, background: t.accent, opacity: SHADE_OPACITY[s] }} />
        ))}
        sets 1-9 · 10-17 · 18+
      </span>
      <span style={item}>
        <span style={{ ...sq, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ width: 3, height: 3, borderRadius: '50%', background: t.textMuted }} />
        </span>
        rest
      </span>
      <span style={item}><span style={{ ...sq, border: `1.5px solid ${colors.amber}` }} />missed</span>
      <span style={item}>
        <span style={{ ...sq, background: t.accent, overflow: 'hidden' }}>
          <span style={{ position: 'absolute', top: 0, right: 0, borderTop: `6px solid ${colors.tick}`, borderLeft: '6px solid transparent' }} />
        </span>
        extra on a rest day
      </span>
    </div>
  );
}
